import type { HomeNotificationKind } from "./HomeNotificationIcon";
import { HomeNotificationStack } from "./HomeNotificationStack";
import { HomeNotificationCard } from "./HomeNotificationCard";
import { hasSellarCreditPaymentAttempt } from "../../components/newOrderSummary/Buttons/sellarCreditPaymentAttempt";
import type { OrderListResponseType } from "@sellar-io/api-v2/types";
import { Button, DataButton } from "@sellar-io/components";
import { useState } from "react";
import { ParentLink } from "../../components/navigation/ParentLink";
import { SellarCreditAddToTabButton } from "../../components/newOrderSummary/Buttons/SellarCreditAddToTabButton";
import {
  Dialog,
  DialogContent,
  DialogNavigation,
} from "../../components/modal/Dialog";
import { ORDER_DETAILS, ORDERS } from "../../constants/routes";
import { useBuyerOrderList } from "../../hooks/api/v2/order/useBuyerOrderList";
import { useOrderDeliveryHistory } from "../../hooks/api/v2/order/useOrderDeliveryHistory";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { FailedCollectionBuyerActionCard } from "../order/AcceptedOrders/FailedCollectionBuyerActionCard";
import { WholeOrderExceptionBuyerActionCard } from "../order/AcceptedOrders/WholeOrderExceptionBuyerActionCard";
import { RetryStripePaymentButton } from "../stockList/checkoutV2/CheckoutV2SuccessPageWithFeed";

type Order = OrderListResponseType[number];

export function HomeOrderNotifications() {
  const query = useBuyerOrderList({
    limit: 20,
    query: [
      { type: "requiredAction", modifier: "EQUALS", value: true },
      { type: "cancelledAt", modifier: "IS_NULL" },
      { type: "deniedAt", modifier: "IS_NULL" },
    ],
  });
  if (query.isError) {
    return (
      <div
        role="alert"
        className="flex flex-wrap items-center gap-3 rounded-lg border bg-white p-4 text-sm"
      >
        <p>We couldn’t check orders requiring your attention.</p>
        <Button size="small" color="secondary" onClick={() => query.refetch()}>
          Retry
        </Button>
        <ParentLink to={`${ORDERS}?tab=Action+required`} className="underline">
          View orders
        </ParentLink>
      </div>
    );
  }
  const orders = query.data?.pages.flat() ?? [];
  return (
    <HomeNotificationStack
      orders={orders.map((order) => ({
        id: order.id,
        content: <HomeOrderNotification order={order} />,
      }))}
      loadMore={
        query.hasNextPage ? (
          <button
            type="button"
            disabled={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
            className="text-xs underline"
          >
            More
          </button>
        ) : null
      }
    />
  );
}

function HomeOrderNotification({ order }: { order: Order }) {
  const details = useOrder(order.id);
  const delivery = useOrderDeliveryHistory(
    order.id,
    order.fulfillmentType === "sellarDistribution",
  );
  const { business } = useSellarCurrentBusiness();
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const fullOrder = details.data;
  const latestResolution = fullOrder
    ? fullOrder.FailedCollectionResolution
    : order.FailedCollectionResolution;
  const resolution =
    latestResolution &&
    latestResolution.state !== "replanned" &&
    latestResolution.state !== "cancelled"
      ? latestResolution
      : null;
  const failedPayment = fullOrder?.PaymentList.find(
    (payment) => payment.status === "failed",
  );
  const isDelivery =
    Boolean(resolution) ||
    Boolean(
      delivery.data?.audience === "buyer" && delivery.data.buyerActionRequired,
    );
  const isCredit = !isDelivery && order.paymentType === "sellarCredit";
  const creditAttempted = hasSellarCreditPaymentAttempt({
    twoOrder: fullOrder ? fullOrder.TwoOrder : order.TwoOrder,
    hasPayment: (fullOrder?.PaymentList ?? order.PaymentList).length > 0,
  });
  const orderUrl = ORDER_DETAILS.href({ orderId: order.id });
  if (
    details.isPending ||
    (order.fulfillmentType === "sellarDistribution" && delivery.isPending)
  ) {
    return (
      <p role="status" className="text-sm text-zinc-500">
        Checking order #{order.id}…
      </p>
    );
  }
  let kind: HomeNotificationKind = failedPayment
    ? "cardFailed"
    : "actionRequired";
  if (isDelivery) {
    kind = "deliveryDelayed";
  } else if (isCredit) {
    kind = creditAttempted ? "creditFailed" : "addToTab";
  }
  return (
    <>
      <HomeNotificationCard
        kind={kind}
        orderReference={
          <ParentLink to={orderUrl} className="font-semibold underline">
            order #{order.id}
          </ParentLink>
        }
        supplierName={order.Supplier.Business.name}
        deliveryDate={
          resolution?.expectedDeliveryAt
            ? new Date(resolution.expectedDeliveryAt)
            : null
        }
        actions={
          <>
            {!isDelivery && isCredit && (
              <SellarCreditAddToTabButton
                orderId={order.id}
                initiator={order.initiator}
                orderDeferredPaymentId={order.Payment?.deferredPaymentId}
                buttonSize="small"
                appearance="slim"
                ctaText={creditAttempted ? "Retry" : "Add to tab"}
                isDisabled={!business?.BusinessUser?.canAccessSellarCredit}
              />
            )}
            {!isCredit && !isDelivery && failedPayment?.paymentIntentId && (
              <RetryStripePaymentButton
                stripeAccountId={fullOrder?.Supplier.stripeAccountId ?? ""}
                supplierName={order.Supplier.Business.name}
                paymentAmount={failedPayment.amount}
                paymentIntentId={failedPayment.paymentIntentId}
                returnPath="/home"
                buttonText="Retry payment"
                appearance="slim"
              />
            )}
            {isDelivery && (
              <DataButton
                border
                size="slim"
                onClick={() => setDeliveryOpen(true)}
              >
                Review delivery options
              </DataButton>
            )}
            <ParentLink
              to={orderUrl}
              className="inline-flex h-8 cursor-pointer items-center rounded-lg px-2 text-sm hover:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-purple-600"
            >
              View order
            </ParentLink>
            {details.isError && (
              <p role="alert" className="text-xs text-red-700">
                Couldn’t load order actions.{" "}
                <button
                  type="button"
                  onClick={() => details.refetch()}
                  className="underline"
                >
                  Retry
                </button>
              </p>
            )}
          </>
        }
      />
      <Dialog active={deliveryOpen} onDismiss={() => setDeliveryOpen(false)}>
        <DialogNavigation
          title={`Order #${order.id}`}
          leftIcon="CLOSE"
          onClickLeftIcon={() => setDeliveryOpen(false)}
        />
        <DialogContent>
          {resolution ? (
            <FailedCollectionBuyerActionCard
              orderId={order.id}
              supplierName={order.Supplier.Business.name}
              resolution={resolution}
              onViewDetails={() => setDeliveryOpen(false)}
            />
          ) : (
            <WholeOrderExceptionBuyerActionCard
              orderId={order.id}
              onViewDetails={() => setDeliveryOpen(false)}
            />
          )}
          <ParentLink
            to={orderUrl}
            className="mt-3 inline-block text-sm underline"
          >
            View order details
          </ParentLink>
        </DialogContent>
      </Dialog>
    </>
  );
}
